import { create, type JsonObject } from "@bufbuild/protobuf";
import { FieldMaskSchema } from "@bufbuild/protobuf/wkt";
import type { PaginationArguments } from "#gen/common/v1/pagination_pb.js";
import { StaticAssetSchema, StaticAssetsService, type CreateStaticAssetsResponse, type ListStaticAssetsResponse, type StaticAsset, type UpdateStaticAssetResponse } from "#gen/staticassets/v1/static_assets_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { APIError, handleError } from "#lib/api/error.js";
import { ASSETS_FOLDER_PATH } from "#lib/api/folder.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

export const MAX_FILE_SIZE = 1024 * 500;

export interface UploadURL {
	url: string;
	uploadUrl: string;
	headers: Record<string, string>;
	id: string;
}

export class StaticAssetClient {
	private client: Client<typeof StaticAssetsService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.staticAssets || "/", StaticAssetsService, options);
	}

	async getStaticAsset(id: string): Promise<StaticAsset> {
		try {
			const res = await this.client.getStaticAsset({ id });
			return res.staticAsset!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getUploadURL(metadata?: JsonObject): Promise<UploadURL> {
		try {
			const url = await this.client.getUploadUrl({ metadata });
			return url;
		} catch (e) {
			throw handleError(e);
		}
	}

	async upload(file: File, meta?: JsonObject): Promise<UploadURL> {
		meta = { ...meta, mimetype: file.type, name: file.name, size: file.size };
		const upload = await this.getUploadURL(meta);
		try {
			const headers = new Headers();
			for (const [key, value] of Object.entries(upload.headers)) {
				headers.append(key, value);
			}
			const res = await fetch(upload.uploadUrl, {
				method: "PUT",
				body: file,
				headers,
			});
			if (!res.ok) {
				throw new APIError(res.statusText, res.status);
			}
			return upload;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createStaticAsset(asset: { staticAssetId: string; uploadUrl: string; orgId: string; workspaceId: string; name?: string; fileSize: number; mimeType: string }) {
		const { staticAssetId, uploadUrl, orgId, workspaceId, name, fileSize, mimeType } = asset;
		try {
			const fileName = name ?? "untitled";
			const staticAsset = create(StaticAssetSchema, { orgId, workspaceId, name: fileName, description: "", state: "ACTIVE", fileName, fileSize, mimeType, staticAssetId, uploadUrl });
			return this.client.createStaticAsset({ staticAsset });
		} catch (e) {
			throw handleError(e);
		}
	}

	async createStaticAssets(orgId: string, workspaceId: string, assets: { name?: string; uploadUrl: string; fileSize: number; mimeType: string; staticAssetId: string }[], folderPath?: string): Promise<CreateStaticAssetsResponse> {
		try {
			const resolvedAssets = assets.map(asset => {
				const fileName = asset.name ?? "untitled";
				return create(StaticAssetSchema, { ...asset, orgId, workspaceId, name: fileName, description: "", state: "ACTIVE", folderPath });
			});
			return this.client.createStaticAssets({ staticAssets: resolvedAssets });
		} catch (e) {
			throw handleError(e);
		}
	}

	async listStaticAssets(args: { orgId: string; workspaceId: string; namePrefix?: string; creatorId?: string; states?: string[]; categories?: string[]; pagination: PaginationArguments; folderPath?: string }, options?: CallOptions): Promise<ListStaticAssetsResponse> {
		const { orgId, workspaceId, namePrefix, creatorId, states, pagination, categories, folderPath } = args;
		try {
			return this.client.listStaticAssets({ orgId, workspaceId, pagination, namePrefix, creatorId, states, categories, folderPath }, options);
		} catch (e) {
			throw handleError(e);
		}
	}

	async archiveStaticAsset(asset: StaticAsset): Promise<UpdateStaticAssetResponse> {
		try {
			return this.client.updateStaticAsset({ staticAsset: create(StaticAssetSchema, { id: asset.id, state: "ASSET_STATE_ARCHIVED", folderPath: ASSETS_FOLDER_PATH, workspaceId: asset.workspaceId, orgId: asset.orgId }), mask: create(FieldMaskSchema, { paths: ["state", "folder_path"] }) });
		} catch (e) {
			throw handleError(e);
		}
	}

	async archiveStaticAssets(assets: StaticAsset[]): Promise<UpdateStaticAssetResponse[]> {
		if (assets.length === 0) {
			return [];
		}
		if (assets.length === 1) {
			return [await this.archiveStaticAsset(assets[0])];
		}
		try {
			const requests = this.client.bulkUpdateStaticAsset({
				requests: assets.map(a => ({
					staticAsset: create(StaticAssetSchema, { id: a.id, state: "ASSET_STATE_ARCHIVED", folderPath: ASSETS_FOLDER_PATH, workspaceId: a.workspaceId, orgId: a.orgId }),
					mask: create(FieldMaskSchema, { paths: ["state", "folder_path"] }),
				})),
			});
			const results: UpdateStaticAssetResponse[] = [];
			for await (const bulkResponse of requests) {
				if (bulkResponse.response) {
					results.push(bulkResponse.response);
				}
			}
			return results;
		} catch (e) {
			throw handleError(e);
		}
	}

	async restoreStaticAsset(asset: StaticAsset): Promise<UpdateStaticAssetResponse> {
		try {
			return this.client.updateStaticAsset({ staticAsset: create(StaticAssetSchema, { id: asset.id, state: "ASSET_STATE_ACTIVE" }), mask: create(FieldMaskSchema, { paths: ["state"] }) });
		} catch (e) {
			throw handleError(e);
		}
	}

	async restoreStaticAssets(assets: StaticAsset[]): Promise<UpdateStaticAssetResponse[]> {
		if (assets.length === 0) {
			return [];
		}
		if (assets.length === 1) {
			return [await this.restoreStaticAsset(assets[0])];
		}
		try {
			const requests = this.client.bulkUpdateStaticAsset({
				requests: assets.map(a => ({
					staticAsset: create(StaticAssetSchema, { id: a.id, state: "ASSET_STATE_ACTIVE" }),
					mask: create(FieldMaskSchema, { paths: ["state"] }),
				})),
			});
			const results: UpdateStaticAssetResponse[] = [];
			for await (const bulkResponse of requests) {
				if (bulkResponse.response) {
					results.push(bulkResponse.response);
				}
			}
			return results;
		} catch (e) {
			throw handleError(e);
		}
	}

	async renameStaticAsset(asset: StaticAsset, name: string): Promise<UpdateStaticAssetResponse> {
		try {
			return this.client.updateStaticAsset({ staticAsset: create(StaticAssetSchema, { id: asset.id, name }), mask: create(FieldMaskSchema, { paths: ["name"] }) });
		} catch (e) {
			throw handleError(e);
		}
	}

	async moveStaticAssets(assets: StaticAsset[], folderPath: string, folderId: string): Promise<UpdateStaticAssetResponse[]> {
		if (assets.length === 0) {
			return [];
		}
		try {
			const requests = this.client.bulkUpdateStaticAsset({
				requests: assets.map(a => ({
					staticAsset: create(StaticAssetSchema, { ...a, folderPath: folderPath, folderId: folderId }),
					mask: create(FieldMaskSchema, { paths: ["folder_path"] }),
				})),
			});
			const results: UpdateStaticAssetResponse[] = [];
			for await (const bulkResponse of requests) {
				if (bulkResponse.response) {
					results.push(bulkResponse.response);
				}
			}
			return results;
		} catch (e) {
			throw handleError(e);
		}
	}
}
